import { useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis } from "recharts";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { PageHeader, Empty } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { compactInr, fmtDate, money } from "@/lib/format";

export default function Inventory() {
  const [summary, setSummary] = useState(null);
  const [aging, setAging] = useState([]);
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api.get("/inventory/stock-summary").then((r) => setSummary(r.data));
    api.get("/inventory/aging").then((r) => setAging(r.data));
  }, []);

  const openLedger = async (row) => {
    const { data } = await api.get(`/inventory/stock-ledger/${row.item_id}`);
    setDetail({ row, lines: data });
  };

  if (!summary) return <p className="text-sm text-muted-foreground">Loading stock…</p>;
  const rows = summary.rows.filter((r) => !q || r.name.toLowerCase().includes(q.toLowerCase()));
  const low = summary.rows.filter((r) => r.below_reorder).length;

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <PageHeader title="Inventory" subtitle="Event-sourced stock ledger with weighted-average valuation and reorder intelligence.">
        <Input placeholder="Filter items…" value={q} onChange={(e) => setQ(e.target.value)}
          className="h-8 w-56 text-sm" data-testid="inventory-search-input" />
      </PageHeader>

      <div className="grid gap-px border border-border bg-border sm:grid-cols-4">
        {[["Stock value", compactInr(summary.total_value)], ["SKUs tracked", summary.rows.length],
          ["Below reorder", low], ["Out of stock", summary.rows.filter((r) => r.closing_qty <= 0).length]].map(([k, v]) => (
          <div key={k} className="bg-background p-4" data-testid={`inv-kpi-${k.replace(/\s/g, "-")}`}>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{k}</p>
            <p className="num mt-1.5 text-xl font-semibold">{v}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="panel overflow-x-auto lg:col-span-2">
          <div className="border-b border-border px-4 py-2.5"><h2 className="font-display text-sm font-bold">Stock summary</h2></div>
          {rows.length === 0 ? (
            <Empty label="No stock movement" hint="Post a purchase bill to bring stock in." />
          ) : (
            <table className="dense-table w-full min-w-[760px]" data-testid="stock-summary-table">
              <thead>
                <tr><th>Item</th><th>HSN</th><th className="text-right">In</th><th className="text-right">Out</th>
                  <th className="text-right">Closing</th><th className="text-right">Avg rate</th>
                  <th className="text-right">Value</th><th>Status</th></tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.item_id} className="cursor-pointer row-in" onClick={() => openLedger(r)} data-testid={`stock-row-${r.item_id}`}>
                    <td className="font-medium">{r.name}</td>
                    <td className="num">{r.hsn || "—"}</td>
                    <td className="num text-right">{r.inward}</td>
                    <td className="num text-right">{r.outward}</td>
                    <td className="num text-right font-medium">{r.closing_qty} {r.uom}</td>
                    <td className="num text-right">{money(r.avg_rate)}</td>
                    <td className="num text-right">{money(r.value)}</td>
                    <td>
                      <span className={`px-1.5 py-0.5 text-[10px] uppercase tracking-widest ${
                        r.status === "Healthy" ? "bg-success/10 text-success" :
                        r.status === "Low" ? "bg-chart-4/10 text-chart-4" : "bg-destructive/10 text-destructive"}`}>
                        {r.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="panel">
          <div className="border-b border-border px-4 py-2.5"><h2 className="font-display text-sm font-bold">Stock ageing (inward value)</h2></div>
          <div className="h-[240px] p-3">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={aging}>
                <CartesianGrid stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="bucket" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tickFormatter={compactInr} tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" width={56} />
                <RTooltip formatter={(v) => money(v)} contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
                <Bar dataKey="value" fill="hsl(var(--chart-1))" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-xl" data-testid="stock-ledger-sheet">
          <SheetHeader><SheetTitle className="font-display">{detail?.row?.name}</SheetTitle></SheetHeader>
          <table className="dense-table mt-4 w-full">
            <thead><tr><th>Date</th><th>Ref</th><th className="text-right">In</th><th className="text-right">Out</th><th className="text-right">Balance</th></tr></thead>
            <tbody>
              {(detail?.lines || []).map((l) => (
                <tr key={l.id}>
                  <td className="num">{fmtDate(l.date)}</td>
                  <td className="text-muted-foreground">{l.ref || "—"}</td>
                  <td className="num text-right">{l.qty_in || "—"}</td>
                  <td className="num text-right">{l.qty_out || "—"}</td>
                  <td className="num text-right font-medium">{l.balance}</td>
                </tr>
              ))}
              {detail?.lines?.length === 0 && <tr><td colSpan={5} className="text-muted-foreground">No movement.</td></tr>}
            </tbody>
          </table>
        </SheetContent>
      </Sheet>
    </div>
  );
}
